import { isNil } from 'lodash';

import { history } from '../../store';

import {
  startLoading,
  receivedClearNotifications,
  receivedGetDealershipSettingsSuccess,
  receivedGetDealershipSettingsFail,
  receivedUpdateDealershipSettingsSuccess,
  receivedUpdateDealershipSettingsFail,
  receivedGetPaymentAccountLegalEntitySuccess,
  receivedGetPaymentAccountLegalEntityFail,
  receivedUpdatePaymentAccountLegalEntitySuccess,
  receivedUpdatePaymentAccountLegalEntityFail,
  receivedUpdatePaymentAccountLegalEntityDocumentSuccess,
  receivedUpdatePaymentAccountLegalEntityDocumentFail,
  receivedMailChimpIntegrationSuccess,
  receivedMailChimpIntegrationFail,
  receivedMailChimpCustomerProgressSuccess,
  receivedMailChimpCustomerProgressFail,
  receivedGetMailChimpAuthUrlSuccess,
  receivedGetDealershipDepartmentsSuccess,
  receivedGetDealershipDepartmentsFail,
  receivedUpdateDealershipDepartmentSuccess,
  receivedUpdateDealershipDepartmentFail,
  receivedCreateDealershipDepartmentSuccess,
  receivedCreateDealershipDepartmentFail,
  receivedDeleteDealershipDepartmentSuccess,
  receivedDeleteDealershipDepartmentFail,
} from './actions';

import {
  authorizeMailChimpIntegration,
  acceptTerms,
  dealershipSettings,
  paymentAccountLegalEntity,
  updateLegalEntity,
  updateLegalEntityDocument,
  pushCustomersToMailChimp as pushCustomersToMailChimpRequest,
  getMailChimpCustomersListProgress as getMailChimpCustomersListProgressRequest,
  getMailChimpAuthUrl as getMailChimpAuthUrlRequest,
  dealershipDepartments,
  patchDealershipDepartment,
  createDealershipDepartment,
  deleteDealershipDepartment,
} from './resources';

import { DealershipDepartment } from 'services/resources';

export const getDealershipSettings = () => (dispatch) => {
  dispatch(startLoading());

  return dealershipSettings()
    .then((response) => {
      dispatch(receivedGetDealershipSettingsSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedGetDealershipSettingsFail(response.data));
      return Promise.reject(response);
    });
};

export const acceptPaymentTerms = (id) => (dispatch) => {
  dispatch(startLoading());

  return acceptTerms(id)
    .then((response) => {
      dispatch(receivedUpdateDealershipSettingsSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedUpdateDealershipSettingsFail(response.data));
      return Promise.reject(response);
    });
};

function sanitizeInboundLegalEntity(legalEntity) {
  const { dob, ...rest } = legalEntity;

  const { year, month, day } = dob;

  let sanitizedDob = { dob: null };

  if (!(isNil(year) || isNil(month) || isNil(day))) {
    sanitizedDob = { dob: `${year}-${month}-${day}` };
  }

  return {
    ...rest,
    ...sanitizedDob,
  };
}

export const getPaymentAccountLegalEntity = (id) => (dispatch) => {
  dispatch(startLoading());

  return paymentAccountLegalEntity(id)
    .then((response) => {
      const legalEntity = sanitizeInboundLegalEntity(response);
      dispatch(receivedGetPaymentAccountLegalEntitySuccess(legalEntity));
      return response;
    })
    .catch((response) => {
      // Stripe errors don't necessarily have `data`
      dispatch(receivedGetPaymentAccountLegalEntityFail(response.data || response));
      return Promise.reject(response);
    });
};

export const updatePaymentAccountLegalEntity = (id, legalEntity) => (dispatch) => {
  dispatch(startLoading());

  return updateLegalEntity(id, legalEntity)
    .then((response) => {
      dispatch(receivedUpdatePaymentAccountLegalEntitySuccess(response));
      return response;
    })
    .catch((response) => {
      let santizedReponseData = { errors: [] };
      const { data } = response;

      if (data.message) {
        santizedReponseData = {
          errors: [data.message],
        };
      } else {
        santizedReponseData = data;
      }

      dispatch(receivedUpdatePaymentAccountLegalEntityFail(santizedReponseData));

      return Promise.reject(response);
    });
};

export const updatePaymentAccountLegalEntityDocument = (id, file, options) => (dispatch) => {
  dispatch(startLoading());

  return updateLegalEntityDocument(id, file, options)
    .then((response) => {
      dispatch(receivedUpdatePaymentAccountLegalEntityDocumentSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedUpdatePaymentAccountLegalEntityDocumentFail(response.data));
      return Promise.reject(response);
    });
};

export const authorizeMailChimp = (id, code) => (dispatch) => {
  dispatch(startLoading());

  return authorizeMailChimpIntegration(id, code)
    .then((response) => dispatch(receivedMailChimpIntegrationSuccess(response)))
    .then(() => history.push('/engage/integrations/mailchimp'))
    .catch((response) => {
      dispatch(receivedMailChimpIntegrationFail(response.data));
      history.push('/engage/integrations/mailchimp');
      return Promise.reject(response);
    });
};

export const pushCustomersToMailChimp = (id) => (dispatch) => {
  dispatch(startLoading());

  return pushCustomersToMailChimpRequest(id)
    .then((response) => dispatch(receivedMailChimpCustomerProgressSuccess(response)))
    .catch((response) => {
      dispatch(receivedMailChimpCustomerProgressFail(response.data));
      return Promise.reject(response);
    });
};

export const getMailChimpCustomersListProgress = (id) => (dispatch) => {
  dispatch(startLoading());

  return getMailChimpCustomersListProgressRequest(id)
    .then((response) => dispatch(receivedMailChimpCustomerProgressSuccess(response)))
    .catch((response) => {
      dispatch(receivedMailChimpCustomerProgressFail(response.data));
      return Promise.reject(response);
    });
};

export const getMailChimpAuthUrl = () => (dispatch) => {
  dispatch(startLoading());

  return getMailChimpAuthUrlRequest().then((response) => dispatch(receivedGetMailChimpAuthUrlSuccess(response)));
};

export const clearNotifications = () => (dispatch) => dispatch(receivedClearNotifications());

export const getDealershipDepartments = () => (dispatch) => {
  dispatch(startLoading());

  return dealershipDepartments()
    .then((response) => {
      dispatch(receivedGetDealershipDepartmentsSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedGetDealershipDepartmentsFail(response.data));
      return Promise.reject(response);
    });
};

export const updateDealershipDepartment = (id: number, department: DealershipDepartment) => (dispatch) => {
  dispatch(startLoading());

  return patchDealershipDepartment(id, department)
    .then((response) => {
      dispatch(receivedUpdateDealershipDepartmentSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedUpdateDealershipDepartmentFail(response.data));
      return Promise.reject(response);
    });
};

export const addDealershipDepartment = (department: DealershipDepartment) => (dispatch) => {
  dispatch(startLoading());

  return createDealershipDepartment(department)
    .then((response) => {
      dispatch(receivedCreateDealershipDepartmentSuccess(response));
      return response;
    })
    .catch((response) => {
      dispatch(receivedCreateDealershipDepartmentFail(response.data));
      return Promise.reject(response);
    });
};

export const removeDealershipDepartment = (id: number) => (dispatch) => {
  dispatch(startLoading());

  return deleteDealershipDepartment(id)
    .then((response) => {
      dispatch(receivedDeleteDealershipDepartmentSuccess(id));
      return response;
    })
    .catch((response) => {
      dispatch(receivedDeleteDealershipDepartmentFail(response.data));
      return Promise.reject(response);
    });
};
